import { Event, type Proposal, type QuoteLine } from '@kalos/kalos-rpc';

import { EventClientService } from '../tools/helpers';

const ACCEPTED_PROPOSAL_SEPARATOR = '\n\n--- Accepted proposal (signed) ---\n';

/**
 * Builds the Services Needed (`Event.description`) string: one non-empty quote line
 * `description` per row (matches how line items are labeled in the proposal UI).
 */
export function formatAcceptedQuoteLinesForServiceNeeded(lines: QuoteLine[]): string {
  return lines
    .map((line) => (line.description || '').trim())
    .filter((s) => s.length > 0)
    .join('\n');
}

/** Prefer the event returned from sign (e.g. new follow-up job); otherwise the job that signed. */
export function resolveEventIdAfterProposalSign(
  proposalResponse: Proposal | undefined,
  fallbackEventId: number,
): number {
  const id = proposalResponse?.event?.id;
  if (typeof id === 'number' && id > 0) return id;
  return fallbackEventId;
}

/**
 * After a proposal is signed, persist accepted line item text into `Event.description`.
 * Existing intake / "Service Needed" text is preserved: signed line descriptions are appended
 * under a separator (never a blind replace).
 */
export async function persistServiceNeededFromSignedQuoteLines(params: {
  acceptedLines: QuoteLine[];
  proposalResponse: Proposal | undefined;
  fallbackEventId: number;
}): Promise<void> {
  const accepted = formatAcceptedQuoteLinesForServiceNeeded(params.acceptedLines);
  if (!accepted) return;

  const eventId = resolveEventIdAfterProposalSign(params.proposalResponse, params.fallbackEventId);

  const existing = await EventClientService.Get(Event.create({ id: eventId }));
  const prior = existing && existing.id > 0 ? (existing.description ?? '').trim() : '';
  const description = prior ? `${prior}${ACCEPTED_PROPOSAL_SEPARATOR}${accepted}` : accepted;

  await EventClientService.Update(
    Event.create({
      id: eventId,
      description,
      fieldMask: ['Description'],
    }),
  );
}
